import { Injectable } from '@angular/core';
import { IAppSession } from '../../core/model/appSessions/appSession';
import { Observable, forkJoin, of, EMPTY } from 'rxjs';
import { IBulkUpdateResponse, IBulkUpdateFailure } from '../../core/model/v2-api/v2-api-wrappers';
import { ResourceBaseService } from '../../core/services/resource-base.service';
import { BsApiEndPoints } from '../../core/services/bs-api/endpoints';
import { HttpClient } from '@angular/common/http';
import { isNullOrUndefined } from 'util';
import { V2AppSession, Lane } from '@bssh/ng-sdk';
import { AnalysisDeliveryStatus } from '../../core/model/appSessions/analysis-status';
import { catchError, map } from 'rxjs/operators';

export interface IChangeDeliveryStatusService {
  canChangeAnalysisDeliveryStatus(analyses: IAppSession[], userId: string): boolean;
  changeAnalysisDeliveryStatus(analyses: IAppSession[], deliveryStatus: AnalysisDeliveryStatus, comments?: string):
    Observable<IBulkUpdateResponse<IAppSession>>;

}

@Injectable({
  providedIn: 'root'
})
export class ChangeDeliveryStatusService extends ResourceBaseService<V2AppSession> implements IChangeDeliveryStatusService {

  constructor(private httpClient: HttpClient) {
    super(httpClient, BsApiEndPoints.ANALYSES_URL);
  }

  /**
   * Checks if the change delivery status featured is allowed on selected analyses.
   * @param analyses The analyses for which the check is to be made.
   */
  public canChangeAnalysisDeliveryStatus(analyses: IAppSession[]): boolean {
    return !isNullOrUndefined(analyses) && analyses.length > 0
      && (
        analyses.length === 1 // for details page
        || this.areSelectionsQcCapable(analyses));
  }

  private areSelectionsQcCapable(selections: IAppSession[]) {

    if (isNullOrUndefined(selections)) {
      return false;
    }
    return selections.every((item) => {
      return !isNullOrUndefined(item.QcStatus);
    });
  }

 /**
  * Changes the analyses delivery status .
  * @param analyses The array of analyses for which the delivery status has to be changed.
  * @param deliveryStatus The status to which the delivery status has to be changed to.
  * @param comments Additional comments.
  */
  public changeAnalysisDeliveryStatus(analyses: IAppSession[], deliveryStatus: AnalysisDeliveryStatus,
                                      comments?: string): Observable<IBulkUpdateResponse<IAppSession>> {
    const analysesUpdateResponse: IBulkUpdateResponse<IAppSession> = {
      SuccessItems: [],
      TotalSuccessCount: 0,
      FailureItems: [],
      TotalFailureCount: 0
    };

    const postData = {
      DeliveryStatus: deliveryStatus.toString(),
      ...!isNullOrUndefined(comments) && { Comment: comments }
    };

    return forkJoin(
      analyses.map((analysis: IAppSession, index: number) => {
        //return this.post(analysis.Id, postData).
        return this.httpClient.post(`${BsApiEndPoints.ANALYSES_URL}${analysis.Id}`, postData).
          pipe(
            catchError(error => {
              const failureObject: IBulkUpdateFailure<IAppSession> = {
                Id: analysis.Id,
                ErrorMessage: error.message,
                ErrorCode: error.status
              };
              analysesUpdateResponse.FailureItems.push(failureObject);
              analysesUpdateResponse.TotalFailureCount++;
              return of(undefined);
            }),
          ).pipe(
            map((response: V2AppSession) => {
              if (response) {
                analysesUpdateResponse.SuccessItems.push(this.toIAppSession(response));
                analysesUpdateResponse.TotalSuccessCount++;
              }
              return EMPTY; // as nothing needs to be returned from this observable, but analysesUpdateResponse needs to returned
            })
          );
      })
    ).pipe(map((response: any) => {
      return analysesUpdateResponse;
    }));
  }

  private toIAppSession(response: V2AppSession): IAppSession {
    return JSON.parse(JSON.stringify(response)) as IAppSession;
  }

}
